import { Component, DestroyRef, inject, input, NgZone } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { SidebarService } from '../../../services/sidebar/sidebar.service';
import { IconComponent } from '../icon/icon.component';
import { HeaderUserComponent } from './sub-components/header-user/header-user.component';
import { HeaderRemindersComponent } from './sub-components/header-reminders/header-reminders.component';
import { HeaderNotificationsComponent } from './sub-components/header-notifications/header-notifications.component';
import { HeaderTasksComponent } from './sub-components/header-tasks/header-tasks.component';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { HeaderNotificationsService } from '../../../services/header/header-notifcations/header-notifications.service';
import { HeaderRemindersService } from '../../../services/header/header-reminders/header-reminders.service';
import { BrandLogoComponent, BrandLogoType } from '../../brand-logo/brand-logo.component';
import { HeaderTaskService } from '../../../services/header/header-task/header-task.service';
import { Router, RouterLink } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import * as Sentry from '@sentry/angular';
import { HeaderHelpComponent } from './sub-components/header-help/header-help.component';
import { HeaderComponentMode } from './header-component-mode';
import { TranslationService } from '../../../../core/services/translation/translation.service';
import { EditorModule } from '@progress/kendo-angular-editor';
import { distinctUntilChanged, Observable, shareReplay, take, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { userSettingsFeature } from '../../../../store/features/user-settings/user-settings-feature';
import { DropDownListModule, SharedDirectivesModule } from '@progress/kendo-angular-dropdowns';
import { LanguageDto } from '../../../../../models/ts/language-dto.model';
import { userSettingsActions } from '../../../../store/features/user-settings/user-settings-actions';
import { UserType } from '../../../../../models/ts/user-type.model';
import { UserSettingsApiService } from '../../../../api/bizzmine/user-settings/user-settings-api.service';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { PwaService } from 'src/app/core/services/pwa/pwa.service';
import { NetworkService } from 'src/app/core/services/network/network.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { networkActions } from 'src/app/store/features/network/network-actions';
import { SyncQueueService } from 'src/app/core/services/sync/sync-queue.service';
import { HeaderSyncComponent } from './sub-components/header-sync/header-sync.component';

@Component({
  selector: 'bizz-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    IconComponent,
    HeaderUserComponent,
    HeaderHelpComponent,
    HeaderRemindersComponent,
    HeaderNotificationsComponent,
    HeaderTasksComponent,
    HeaderSyncComponent,
    TooltipModule,
    TranslatePipe,
    BrandLogoComponent,
    RouterLink,
    AsyncPipe,
    EditorModule,
    DropDownListModule,
    SharedDirectivesModule
  ]
})
@Sentry.TraceClass({ name: 'HeaderComponent' })
export class HeaderComponent {
  public readonly BrandLogoType = BrandLogoType;
  public settingsUri: string;
  public mode = input<HeaderComponentMode>(HeaderComponentMode.DEFAULT);
  public readonly HeaderComponentMode = HeaderComponentMode;
  public selectedLanguage: number | undefined;
  public selectedLanguageDto: LanguageDto | undefined;
  protected readonly BrandLogoComponent = BrandLogoComponent;
  protected readonly environment = environment;
  public authService = inject(AuthService);
  private sidebarService = inject(SidebarService);
  private headerTaskService = inject(HeaderTaskService);
  private headerNotificationsService = inject(HeaderNotificationsService);
  private headerRemindersService = inject(HeaderRemindersService);
  private translationService = inject(TranslationService);
  private store$ = inject(Store);
  public languageOptions$ = this.getSelectedLanguages();
  private userSettingsApiService = inject(UserSettingsApiService);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  public pwaService = inject(PwaService);
  public networkService = inject(NetworkService);
  private destroyRef = inject(DestroyRef);
  private actions$ = inject(Actions);
  public syncQueueService = inject(SyncQueueService);

  protected readonly UserType = UserType;

  public get userType(): UserType {
    return this.store$.selectSignal(userSettingsFeature.selectUserType)();
  }

  // public totalSyncItems = signal(0);

  public constructor() {
    this.userSettingsApiService.getUserSettings().pipe(take(1)).subscribe((userSettings) => {
      const userType = userSettings?.UserType;
      if (userType != undefined && userType != UserType.AnonymousUser && userType != UserType.ExternalUser) {
        this.ngZone.runOutsideAngular(() => {
          if (this.pwaService.isPwa()) {
            this.actions$.pipe(
              ofType(networkActions.statusChanged),
              distinctUntilChanged(),
              takeUntilDestroyed(this.destroyRef)
            ).subscribe({
              next: ({ isOnline }) => {
                if (isOnline) {
                  this.headerTaskService.startPolling();
                  this.headerRemindersService.startPolling();
                  this.headerNotificationsService.startPolling();
                } else {
                  this.headerTaskService.stopPolling();
                  this.headerRemindersService.stopPolling();
                  this.headerNotificationsService.stopPolling();
                }
              }
            });
          } else {
            this.headerTaskService.startPolling();
            this.headerRemindersService.startPolling();
            this.headerNotificationsService.startPolling();
          }
        });
      }
    });
    this.settingsUri = AuthService.getSettingsUri;
  }

  public toggleSidebar(force?: boolean): void {
    this.sidebarService.toggleSidebar(force);
  }

  public brandClicked($event: MouseEvent): void {
    $event.preventDefault();
    const newTab = $event && ($event.ctrlKey || $event.metaKey);
    if (newTab) {
      const url = this.router.createUrlTree(['/']);
      window.open(url.toString(), '_blank');
    } else {
      this.router.navigate(['/']);
    }

  }

  public changeLanguageSelection(language: { ID: number | undefined }): void {
    this.selectedLanguage = language.ID;
    if (this.selectedLanguage != undefined && this.selectedLanguage > 0) {
      this.store$.dispatch(userSettingsActions.setAnonymousPortalLanguage({ language: this.selectedLanguage }));
    }
  }

  private getSelectedLanguages(): Observable<LanguageDto[]> {
    return this.translationService.getLanguageOptions(window.location.hostname.split('.')[0]).pipe(tap((options) => {
      const anon = this.store$.selectSignal(userSettingsFeature.selectAnonymousPortalUserLanguage)();
      const user = this.store$.selectSignal(userSettingsFeature.selectUserLanguagesID)();
      this.selectedLanguage = anon != undefined && anon > 0 ? anon : user > 0 ? user : options != undefined ? options?.find(_ => _.IsSystemLanguage)?.ID ?? options?.[0]?.ID : undefined;
      this.selectedLanguageDto = options?.find(_ => _.ID == this.selectedLanguage);
    }), shareReplay(1));
  }
}
